import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { LocalTrack, Room } from 'livekit-client';
import { ApiError, request } from './api';
import { useAuth } from './auth';
import type { TranslationKey } from './i18n';
import {
  MicrophoneRefused,
  askForMicrophone,
  callDeviceId,
  callsApi,
  endedText,
  startRingback,
  startRinging,
  stopRingback,
  stopRinging,
  type IncomingCall,
  type LiveCall,
  type LiveState,
} from './calls';

/**
 * Every page's part in calls (2026-10-29): it listens for a call ringing, rings,
 * answers on this tab, joins the call's room and leaves it — mounted once, in the
 * layout, so a call goes on whatever page you move to.
 *
 * - **The ring** is a long poll (`GET /calls/events`), held by every open tab, so
 *   a call rings the moment it is made and stops on every tab once answered on one —
 *   or at its deadline, read on the API's clock, when the API is out of reach to say so.
 *   The caller hears it ringing too — the ringback tone, on the tab that called,
 *   until someone answers or it is called off.
 * - **The room**: only the tab that answered — or called — joins it, with a token
 *   the API gives it. The microphone stays off until the API says the recorder is
 *   confirmed (`maySpeak`) *and* LiveKit has given this tab the right to publish:
 *   nothing is ever said before the call is recorded.
 * - **Fail closed**: the moment the API says the call is no longer live — ended,
 *   failed, its recorder lost — the room is left. If the API cannot be reached for
 *   20 s during a call, it is left too: no call goes on that nobody can vouch for.
 *   So is a call still ringing out from this tab — called off, its ringback stopped:
 *   no tone plays on for a call nobody can say is still ringing.
 * - **An end is final for the tab** (the production hardening, 2026-10-29): a call
 *   hung up here, ended by the API or left for the API's silence is never joined
 *   again by this tab, nor heard from — no reconnect, no token, no microphone that
 *   was on its way — unless the person answers it anew, which the API authorises
 *   afresh. Left for the API's silence, the tab hangs up as soon as the API answers:
 *   a group call goes on without it.
 * - **A shared screen** (2026-11-02: "add share screen feature in calls") is under
 *   the microphone's rule: shared only once the API says the call is recorded *and*
 *   LiveKit lets this tab publish a screen; stopped the moment either is gone, the
 *   call ends or the room is left. Its picture only — never the sound of a tab or
 *   the system, so nothing is heard in a call that the recording does not hold.
 *   The recording is the call's sound: the screen itself is not kept.
 *
 * livekit-client is loaded only when a call starts: nobody pays for it otherwise.
 */

export type Connection = 'idle' | 'connecting' | 'connected' | 'reconnecting';

/** A screen someone else on the call shares, as this tab receives it. */
export interface SharedScreen {
  /** Their room identity: `user_<id>`, or `client_<account>` (calls.ts `roomIdentity`). */
  identity: string;
  /** Their name as LiveKit has it — the call's people say it in the reader's language. */
  name: string;
  /** The shared screen's track in the room. */
  sid: string;
  /** Shows it in a video element; what it returns takes it out again. */
  attach: (el: HTMLVideoElement) => () => void;
}

interface CallSession {
  ready: boolean;
  incoming: IncomingCall[];
  current: LiveCall | null;
  /** The server's clock minus this one's, so a call's time reads the same everywhere. */
  serverOffset: number;
  connection: Connection;
  muted: boolean;
  /** The microphone is on and sent. */
  micOn: boolean;
  /** The browser refused the microphone. */
  micDenied: boolean;
  /** The browser will not play the call until someone clicks. */
  audioBlocked: boolean;
  /** Who is speaking now (room identities). */
  speaking: ReadonlySet<string>;
  /** This browser can share a screen at all — a phone's cannot. */
  canShareScreens: boolean;
  /** This tab may share a screen now: in the room, the call recorded, LiveKit allowing it. */
  mayShare: boolean;
  /** This tab shares a screen, a window or a tab in the call. */
  sharing: boolean;
  /** What the others on the call share, as this tab receives it — the first shared first. */
  screens: SharedScreen[];
  /** How the last call ended, shown a moment. */
  ended: { callId: string; text: TranslationKey } | null;
  start: (userIds: string[]) => Promise<LiveCall>;
  accept: (callId: string) => Promise<void>;
  decline: (callId: string) => Promise<void>;
  hangUp: () => Promise<void>;
  toggleMute: () => void;
  /** Asks the browser for a screen, a window or a tab, and shares it — nothing when the person picks none. */
  startSharing: () => Promise<void>;
  stopSharing: () => void;
  enableAudio: () => void;
  dismissEnded: () => void;
}

const CallSessionContext = createContext<CallSession | null>(null);

/** How long a call goes on with the API out of reach before this tab leaves it. */
const LOST_SERVER_MS = 20_000;
/** How often the API is asked about the call while in it. */
const HEARTBEAT_MS = 5_000;
/**
 * How often its silence is looked at — on its own clock, not the heartbeat's: checked
 * only as each beat asks again, the 20 s came out as 25 (2026-10-29, the safety checks).
 */
const WATCH_MS = 1_000;

export function CallSessionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const deviceId = useMemo(callDeviceId, []);
  const [live, setLive] = useState<LiveState | null>(null);
  const [serverOffset, setServerOffset] = useState(0);
  const [connection, setConnection] = useState<Connection>('idle');
  const [muted, setMuted] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [micDenied, setMicDenied] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [speaking, setSpeaking] = useState<ReadonlySet<string>>(new Set());
  const [sharing, setSharing] = useState(false);
  const [screens, setScreens] = useState<SharedScreen[]>([]);
  const [ended, setEnded] = useState<CallSession['ended']>(null);
  /**
   * This tab's time when it last looked: at each answer of the API's, and at the deadline of each call
   * ringing for it. What still rings is read against it, on the API's clock (`serverOffset`).
   */
  const [clock, setClock] = useState(() => Date.now());
  // Bumped when LiveKit changes this tab's permissions, to look at the microphone again.
  const [permissionTick, setPermissionTick] = useState(0);
  const roomRef = useRef<{ callId: string; room: Room } | null>(null);
  /** livekit-client, once a call has loaded it. */
  const lkRef = useRef<typeof import('livekit-client') | null>(null);
  const joiningRef = useRef<string | null>(null);
  const audioBox = useRef<HTMLDivElement | null>(null);
  const lastReached = useRef(Date.now());
  /**
   * The calls this tab is done with — hung up here, ended by the API, or left when the API went quiet.
   * Final for the tab: it never joins one of them again, nor sends a sound into it, whatever the API
   * says next — unless the person answers it anew (`accept`), which the API authorises afresh.
   */
  const finishedRef = useRef<ReadonlySet<string>>(new Set());
  const [finished, setFinished] = useState<ReadonlySet<string>>(finishedRef.current);
  /**
   * Moved on at every end and every room left: whatever was on its way for what came before — a room
   * connecting, a token, a microphone opening — finds it moved on when it arrives, and undoes itself.
   */
  const generation = useRef(0);
  /** The server's time of the newest state used: an answer that arrives late never undoes a newer one. */
  const newest = useRef(0);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const apply = useCallback((state: LiveState) => {
    const now = Date.now();
    // Any answer is word from the API — an old one too.
    lastReached.current = now;
    const at = Date.parse(state.serverTime);
    if (at < newest.current) return;
    newest.current = at;
    setLive(state);
    setServerOffset(at - now);
    setClock(now);
  }, []);

  const finish = useCallback((callId: string) => {
    if (finishedRef.current.has(callId)) return;
    finishedRef.current = new Set([...finishedRef.current, callId]);
    generation.current++;
    setFinished(finishedRef.current);
  }, []);

  /** Answering a call is the person's own act: it may be this tab's again, once the API says so. */
  const reopen = useCallback((callId: string) => {
    if (!finishedRef.current.has(callId)) return;
    finishedRef.current = new Set([...finishedRef.current].filter((id) => id !== callId));
    setFinished(finishedRef.current);
  }, []);

  const refresh = useCallback(async () => {
    try {
      apply(await callsApi.live(deviceId));
    } catch {
      // The poll catches up.
    }
  }, [apply, deviceId]);

  // ---- the ring: a long poll, every tab, the whole time someone is signed in ----
  useEffect(() => {
    if (!user) return;
    let stopped = false;
    let cursor: string | undefined;
    const controller = new AbortController();
    void (async () => {
      let delay = 0;
      while (!stopped) {
        try {
          const res = await request<{ cursor: string; live: LiveState }>('/calls/events', {
            params: { since: cursor, deviceId },
            signal: controller.signal,
          });
          cursor = res.cursor;
          delay = 0;
          apply(res.live);
        } catch {
          if (stopped) break;
          // Calls not on this server, a restart, the network: ask again, slower and slower.
          delay = Math.min(30_000, delay ? delay * 2 : 2_000);
          await new Promise((r) => setTimeout(r, delay));
        }
      }
    })();
    return () => {
      stopped = true;
      controller.abort();
    };
  }, [user, deviceId, apply]);

  // A call this tab is done with is no call of this tab's, whatever the API last said of it.
  const reported = live?.current ?? null;
  const current = reported && !finished.has(reported.id) ? reported : null;
  // A call ringing for this person rings until its deadline, on the API's clock, and never past it — though
  // the API, out of reach, cannot say it stopped: by then it can no longer be answered (2026-10-29).
  const incoming = useMemo(
    () => (live?.incoming ?? []).filter((call) => !(Date.parse(call.ringUntil) <= clock + serverOffset)),
    [live, clock, serverOffset],
  );
  // Looked at again at the next deadline.
  useEffect(() => {
    const next = Math.min(...incoming.map((call) => Date.parse(call.ringUntil) - serverOffset).filter(Number.isFinite));
    if (!Number.isFinite(next)) return;
    const timer = window.setTimeout(() => setClock(Date.now()), next - Date.now());
    return () => window.clearTimeout(timer);
  }, [incoming, serverOffset]);

  // ---- ringing ----
  useEffect(() => {
    if (incoming.length > 0 && !current) startRinging();
    else stopRinging();
    return stopRinging;
  }, [incoming.length, current]);

  // ---- the ringback: the caller hears the call ringing, on the tab that made it, until someone answers ----
  const callingOut = !!current && current.status === 'RINGING' && current.me.isCaller && current.me.thisDevice;
  useEffect(() => {
    if (!callingOut) return;
    startRingback();
    return stopRingback;
  }, [callingOut]);

  // ---- the room ----
  const leaveRoom = useCallback(async () => {
    generation.current++;
    joiningRef.current = null;
    const held = roomRef.current;
    roomRef.current = null;
    if (audioBox.current) audioBox.current.replaceChildren();
    setConnection('idle');
    setMicOn(false);
    setSharing(false);
    setScreens([]);
    setSpeaking(new Set());
    setAudioBlocked(false);
    if (held) await closeRoom(held.room);
  }, []);

  const joinRoom = useCallback(
    async (callId: string) => {
      const gen = generation.current;
      joiningRef.current = callId;
      setConnection('connecting');
      // Moved on while it was on its way — an end, a room left, another call: it lets go of what it got.
      const stale = () => gen !== generation.current || finishedRef.current.has(callId);
      let room: Room | null = null;
      try {
        const lib = await import('livekit-client');
        const { Room, RoomEvent, Track } = lib;
        lkRef.current = lib;
        if (stale()) return;
        const grant = await callsApi.mediaToken(callId, deviceId);
        if (stale()) return;
        room = new Room({
          adaptiveStream: false,
          dynacast: false,
          audioCaptureDefaults: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          // Two connections, LiveKit's classic way, not the single one livekit-client 2.x tries first.
          // On the single one the server answers Firefox's offer with its own payload numbers, and
          // Firefox drops what arrives: Firefox to Firefox, a call was connected and silent both
          // ways (2026-10-29; livekit/livekit#4599 for audio, #5030 for video, whose only fix on
          // the client was this). Chrome and Edge are fine either way.
          singlePeerConnection: false,
        });
        const own = room;
        own.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
          if (stale()) return;
          if (track.kind === Track.Kind.Audio) {
            const el = track.attach();
            el.dataset.identity = participant.identity;
            audioBox.current?.appendChild(el);
          } else if (track.kind === Track.Kind.Video && pub.source === Track.Source.ScreenShare) {
            // Someone's shared screen: the page shows it (ScreenStage), in its own video element.
            const screen: SharedScreen = {
              identity: participant.identity,
              name: participant.name || participant.identity,
              sid: pub.trackSid,
              attach: (el) => {
                track.attach(el);
                return () => void track.detach(el);
              },
            };
            setScreens((list) => [...list.filter((s) => s.sid !== screen.sid), screen]);
          }
        });
        own.on(RoomEvent.TrackUnsubscribed, (track, pub) => {
          if (track.kind === Track.Kind.Audio) {
            for (const el of track.detach()) el.remove();
          } else {
            // The page's own video element is the page's to take away: only let go of it here.
            track.detach();
            setScreens((list) => list.filter((s) => s.sid !== pub.trackSid));
          }
        });
        own.on(RoomEvent.ParticipantDisconnected, (participant) => {
          if (!stale()) setScreens((list) => list.filter((s) => s.identity !== participant.identity));
        });
        // This tab's own screen, published — or no longer: stopped here, by the browser's own "Stop sharing", by LiveKit.
        const ownScreen = () => !stale() && setSharing(sharesScreen(own, lib));
        own.on(RoomEvent.LocalTrackPublished, ownScreen);
        own.on(RoomEvent.LocalTrackUnpublished, (pub) => {
          // However it left the room — LiveKit's server taking it out too — a screen no longer shared is no longer captured.
          if (pub.source === Track.Source.ScreenShare || pub.source === Track.Source.ScreenShareAudio) pub.track?.stop();
          ownScreen();
        });
        own.on(RoomEvent.ParticipantPermissionsChanged, () => !stale() && setPermissionTick((n) => n + 1));
        own.on(RoomEvent.Reconnecting, () => !stale() && setConnection('reconnecting'));
        own.on(RoomEvent.SignalReconnecting, () => !stale() && setConnection('reconnecting'));
        own.on(RoomEvent.Reconnected, () => !stale() && setConnection('connected'));
        own.on(RoomEvent.AudioPlaybackStatusChanged, () => !stale() && setAudioBlocked(!own.canPlaybackAudio));
        own.on(RoomEvent.ActiveSpeakersChanged, (speakers) => !stale() && setSpeaking(new Set(speakers.map((s) => s.identity))));
        // Dropped by LiveKit while the call is still this tab's: out of the room; the API says whether to join again.
        own.on(RoomEvent.Disconnected, () => {
          if (roomRef.current?.room !== own) return;
          roomRef.current = null;
          if (audioBox.current) audioBox.current.replaceChildren();
          setConnection('idle');
          setMicOn(false);
          setSharing(false);
          setScreens([]);
          void closeRoom(own);
        });
        await own.connect(grant.url, grant.token, { autoSubscribe: true });
        if (stale()) {
          await closeRoom(own);
          return;
        }
        roomRef.current = { callId, room: own };
        joiningRef.current = null;
        setConnection('connected');
        setAudioBlocked(!own.canPlaybackAudio);
        setPermissionTick((n) => n + 1);
      } catch {
        // A room half made is never left half open.
        if (room) await closeRoom(room);
        if (!stale() && joiningRef.current === callId) {
          joiningRef.current = null;
          setConnection('idle');
        }
      }
    },
    [deviceId],
  );

  // Join the call's room on the tab that answered; leave it the moment the call is not live for this tab.
  const shouldJoin = !!current && current.me.thisDevice && (current.status === 'PREPARING' || current.status === 'ACTIVE');
  const currentId = current?.id ?? null;
  useEffect(() => {
    if (!shouldJoin || !currentId) {
      if (roomRef.current || joiningRef.current) void leaveRoom();
      return;
    }
    if (roomRef.current?.callId === currentId || joiningRef.current === currentId) return;
    if (roomRef.current) void leaveRoom();
    void joinRoom(currentId);
  }, [shouldJoin, currentId, joinRoom, leaveRoom]);

  // A dropped room while the call is still live: join again with a fresh token.
  useEffect(() => {
    if (connection !== 'idle' || !shouldJoin || !currentId || joiningRef.current) return;
    const retry = window.setTimeout(() => void joinRoom(currentId), 3_000);
    return () => window.clearTimeout(retry);
  }, [connection, shouldJoin, currentId, joinRoom]);

  // ---- the microphone: on only once the recorder is confirmed and LiveKit allows it ----
  const maySpeak = !!current && current.status === 'ACTIVE' && current.me.maySpeak;
  useEffect(() => {
    const held = roomRef.current;
    if (!held || connection !== 'connected') return;
    const gen = generation.current;
    const allowed = maySpeak && !!held.room.localParticipant.permissions?.canPublish;
    const want = allowed && !muted;
    let cancelled = false;
    held.room.localParticipant.setMicrophoneEnabled(want).then(
      () => {
        // The call ended, or its room was left, while the microphone was opening (a slow permission
        // prompt, a slow device): closed again at once, and its track stopped — never sent.
        if (want && (gen !== generation.current || roomRef.current !== held)) {
          stopOwnTracks(held.room);
          void held.room.localParticipant.setMicrophoneEnabled(false).catch(() => undefined);
          return;
        }
        if (cancelled) return;
        setMicOn(want);
        if (want) setMicDenied(false);
      },
      () => {
        if (cancelled) return;
        setMicOn(false);
        if (want) setMicDenied(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [connection, maySpeak, muted, permissionTick]);

  // ---- a shared screen: the microphone's rule — the call recorded, and LiveKit letting this tab share one ----
  const canShareScreens = useMemo(() => typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function', []);
  // Read again by a share that waited on the browser's picker: the API's word as it is now, not as it was then.
  const maySpeakRef = useRef(maySpeak);
  maySpeakRef.current = maySpeak;
  const [shareAllowed, setShareAllowed] = useState(false);
  useEffect(() => {
    const held = roomRef.current;
    const lib = lkRef.current;
    // Through a reconnection too: LiveKit keeps what this tab publishes while it gets the connection back.
    setShareAllowed(maySpeak && connection !== 'idle' && !!held && !!lib && allowsScreen(held.room.localParticipant.permissions, lib));
  }, [connection, maySpeak, permissionTick]);

  const stopSharing = useCallback(() => {
    const held = roomRef.current;
    const lib = lkRef.current;
    if (held && lib) stopScreen(held.room, lib);
    setSharing(false);
  }, []);

  // The right to share gone — the recording no longer vouched for, LiveKit taking it back: the screen goes with it.
  useEffect(() => {
    if (sharing && !shareAllowed) stopSharing();
  }, [sharing, shareAllowed, stopSharing]);

  const startSharing = useCallback(async () => {
    const held = roomRef.current;
    const lib = lkRef.current;
    if (!held || !lib || !canShareScreens) return;
    const gen = generation.current;
    const allowed = () =>
      gen === generation.current && roomRef.current === held && maySpeakRef.current && allowsScreen(held.room.localParticipant.permissions, lib);
    if (!allowed() || sharesScreen(held.room, lib)) return;
    let tracks: LocalTrack[];
    try {
      tracks = await held.room.localParticipant.createScreenTracks({
        audio: false,
        systemAudio: 'exclude',
        // The page itself may be what is shown — a ticket, a report.
        selfBrowserSurface: 'include',
        surfaceSwitching: 'include',
        // Text kept sharp rather than motion smooth: what is shared is read.
        contentHint: 'detail',
      });
    } catch (e) {
      // The browser's picker closed with nothing chosen, or sharing refused in its settings: nothing to share.
      if (e instanceof DOMException && (e.name === 'NotAllowedError' || e.name === 'AbortError')) return;
      throw e;
    }
    // Its picture alone: any sound the browser gave all the same is stopped at once, never sent.
    const picture = tracks.find((t) => t.source === lib.Track.Source.ScreenShare) ?? null;
    for (const t of tracks) if (t !== picture) t.stop();
    if (!picture) return;
    // The call ended, its room was left or the right to share went while the picker was open: let go, never sent.
    if (!allowed()) {
      picture.stop();
      return;
    }
    try {
      await held.room.localParticipant.publishTrack(picture, { source: lib.Track.Source.ScreenShare });
    } catch (e) {
      picture.stop();
      if (!allowed()) return;
      throw e;
    }
    if (!allowed()) {
      stopScreen(held.room, lib);
      picture.stop();
      return;
    }
    setSharing(true);
  }, [canShareScreens]);
  const mayShare = canShareScreens && shareAllowed && connection === 'connected';

  /**
   * This tab has left: the API is told — hung up — as soon as it answers, a few times over two
   * minutes. The call goes on without this tab when others are still on it (a group); one to one, it
   * ends, as it would once the API saw the tab gone; still ringing, it is called off. Never a way back
   * in: that is answering anew.
   */
  const leaveOnServer = useCallback(async (callId: string) => {
    for (let attempt = 0; attempt < 6 && mounted.current; attempt++) {
      try {
        await callsApi.hangUp(callId);
        return;
      } catch (e) {
        // Over already, or no longer this person's: nothing to leave.
        if (e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 408 && e.status !== 429) return;
      }
      await new Promise((r) => setTimeout(r, 2_000 * 2 ** attempt));
    }
  }, []);

  // ---- fail closed: the API out of reach for 20 s during a call ends it here, for good ----
  // So it does a call still ringing out from this tab (2026-10-29), and its ringback with it. The long poll
  // is held 25 s, so a ringing call is heard from by the heartbeat too.
  useEffect(() => {
    if ((!shouldJoin && !callingOut) || !currentId) return;
    const id = currentId;
    const ringing = callingOut;
    lastReached.current = Date.now();
    const beat = window.setInterval(() => void refresh(), HEARTBEAT_MS);
    const watch = window.setInterval(() => {
      if (Date.now() - lastReached.current <= LOST_SERVER_MS) return;
      window.clearInterval(watch);
      finish(id);
      void leaveRoom();
      setEnded({ callId: id, text: ringing ? 'calls.ended.lostServerRinging' : 'calls.ended.lostServer' });
      void leaveOnServer(id);
    }, WATCH_MS);
    return () => {
      window.clearInterval(beat);
      window.clearInterval(watch);
    };
  }, [shouldJoin, callingOut, currentId, refresh, leaveRoom, finish, leaveOnServer]);

  // ---- how the last call ended ----
  const previous = useRef<string | null>(null);
  useEffect(() => {
    const before = previous.current;
    previous.current = currentId;
    if (!before || before === currentId) return;
    // Gone from the API's word, or from this tab's: over for this tab, for good.
    finish(before);
    setMuted(false);
    void queryClient.invalidateQueries({ queryKey: ['calls', 'history'] });
    void callsApi
      .detail(before)
      .then((call) => setEnded((e) => e ?? { callId: before, text: call.status === 'FAILED' ? endedText(call.failureCode) : endedText(call.endedReason) }))
      .catch(() => undefined);
  }, [currentId, queryClient, finish]);
  useEffect(() => {
    if (!ended) return;
    const hide = window.setTimeout(() => setEnded(null), 9_000);
    return () => window.clearTimeout(hide);
  }, [ended]);

  // Signing out leaves any call.
  useEffect(() => {
    if (!user) {
      void leaveRoom();
      setLive(null);
    }
  }, [user, leaveRoom]);

  // ---- what the pages do ----
  // A call needs a microphone that works: without one the call is not made or answered, and the person is told why.
  const needMicrophone = useCallback(async () => {
    const mic = await askForMicrophone();
    if (mic !== 'granted') throw new MicrophoneRefused(mic);
    setMicDenied(false);
  }, []);

  const start = useCallback(
    async (userIds: string[]) => {
      await needMicrophone();
      setEnded(null);
      const call = await callsApi.create(userIds, deviceId);
      await refresh();
      return call;
    },
    [deviceId, refresh, needMicrophone],
  );

  // Refused, the call keeps ringing: allowed in the address bar, Accept answers it.
  const accept = useCallback(
    async (callId: string) => {
      await needMicrophone();
      setEnded(null);
      // Asked into a call this tab once left (added again), answering is what lets it back in — the API decides.
      reopen(callId);
      try {
        await callsApi.accept(callId, deviceId);
      } finally {
        await refresh();
      }
    },
    [deviceId, refresh, needMicrophone, reopen],
  );

  const decline = useCallback(
    async (callId: string) => {
      try {
        await callsApi.decline(callId);
      } finally {
        await refresh();
      }
    },
    [refresh],
  );

  // Hung up here: over for this tab before anything else — whatever the API answers, or fails to.
  const hangUp = useCallback(async () => {
    const id = currentId;
    if (id) finish(id);
    await leaveRoom();
    if (id) {
      try {
        await callsApi.hangUp(id);
      } catch {
        void leaveOnServer(id);
      } finally {
        await refresh();
      }
    }
  }, [currentId, finish, leaveRoom, leaveOnServer, refresh]);

  const value = useMemo<CallSession>(
    () => ({
      ready: live !== null,
      incoming,
      current,
      serverOffset,
      connection,
      muted,
      micOn,
      micDenied,
      audioBlocked,
      speaking,
      canShareScreens,
      mayShare,
      sharing,
      screens,
      ended,
      start,
      accept,
      decline,
      hangUp,
      toggleMute: () => setMuted((m) => !m),
      startSharing,
      stopSharing,
      enableAudio: () => void roomRef.current?.room.startAudio().then(() => setAudioBlocked(false)),
      dismissEnded: () => setEnded(null),
    }),
    [
      live,
      incoming,
      current,
      serverOffset,
      connection,
      muted,
      micOn,
      micDenied,
      audioBlocked,
      speaking,
      canShareScreens,
      mayShare,
      sharing,
      screens,
      ended,
      start,
      accept,
      decline,
      hangUp,
      startSharing,
      stopSharing,
    ],
  );

  return (
    <CallSessionContext.Provider value={value}>
      {children}
      {/* The other people's voices: LiveKit's audio elements, never shown. */}
      <div ref={audioBox} hidden aria-hidden />
    </CallSessionContext.Provider>
  );
}

/**
 * A room this tab is done with: deaf to it first (none of its events reaches the page again), then
 * disconnected — which also fails a microphone still being published, whose track LiveKit then stops —
 * and every track of this tab's own stopped.
 */
async function closeRoom(room: Room): Promise<void> {
  room.removeAllListeners();
  await room.disconnect(true).catch(() => undefined);
  stopOwnTracks(room);
}

/** Every track this tab publishes in a room, stopped — its microphone off, its screen no longer shared, whatever LiveKit still holds of it. */
function stopOwnTracks(room: Room): void {
  for (const publication of room.localParticipant.trackPublications.values()) publication.track?.stop();
}

type LiveKit = typeof import('livekit-client');

/** LiveKit lets this tab share a screen: it may publish, and a screen is among what it may (none named is any). */
function allowsScreen(permissions: Room['localParticipant']['permissions'], lib: LiveKit): boolean {
  if (!permissions?.canPublish) return false;
  const sources = permissions.canPublishSources ?? [];
  return sources.length === 0 || sources.some((s) => lib.Track.sourceFromProto(s) === lib.Track.Source.ScreenShare);
}

function sharesScreen(room: Room, lib: LiveKit): boolean {
  return [...room.localParticipant.trackPublications.values()].some((p) => p.source === lib.Track.Source.ScreenShare && !!p.track);
}

/** This tab's shared screen taken out of the room and stopped — the browser's "sharing" bar gone with it. */
function stopScreen(room: Room, lib: LiveKit): void {
  for (const publication of [...room.localParticipant.trackPublications.values()]) {
    const track = publication.track;
    if (!track || (publication.source !== lib.Track.Source.ScreenShare && publication.source !== lib.Track.Source.ScreenShareAudio)) continue;
    void room.localParticipant.unpublishTrack(track, true).catch(() => undefined);
    track.stop();
  }
}

export function useCallSession(): CallSession {
  const ctx = useContext(CallSessionContext);
  if (!ctx) throw new Error('useCallSession must be used inside <CallSessionProvider>.');
  return ctx;
}
